import {
  githubDarkTheme,
  githubLightTheme,
  JsonEditor as ReactJsonEditor,
  NodeData,
} from 'json-edit-react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CheckIcon,
  XMarkIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { DropEffect } from '@/components/drag-drop';
import { useTheme } from '@/hooks/use-theme';
import './json-editor.css';
import clsx from 'clsx';

interface JsonEditorProps {
  data: Record<string, unknown>;
  mode?: 'edit' | 'view';
  onUpdate?: (data: unknown) => void;
  className?: string;
}

export function JsonEditor({
  className,
  data,
  mode = 'edit',
  onUpdate,
}: JsonEditorProps) {
  const { theme } = useTheme('light');

  const editorTheme = theme === 'dark' ? githubDarkTheme : githubLightTheme;

  const baseClassName = clsx(
    'block w-full rounded-md border-0 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6 max-h-[300px] overflow-y-auto !max-w-[none] bg-white dark:bg-gray-800',
    className
  );

  return (
    <ReactJsonEditor
      className={baseClassName}
      data={data}
      theme={editorTheme}
      icons={{
        add: <PlusIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />,
        edit: (
          <PencilIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
        ),
        delete: (
          <TrashIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
        ),
        copy: (
          <ClipboardDocumentListIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
        ),
        ok: <CheckIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />,
        cancel: (
          <XMarkIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
        ),
        chevron: (
          <ChevronDownIcon className="h-5 w-5 text-gray-900 dark:text-gray-100" />
        ),
      }}
      customButtons={[
        {
          Element: (props) => <DragJsonButton nodeData={props.nodeData} />,
          onClick: () => {},
        },
      ]}
      restrictAdd={mode === 'view'}
      restrictDelete={mode === 'view'}
      restrictEdit={mode === 'view'}
      enableClipboard={true}
      onUpdate={(data) => {
        onUpdate?.(data.newData);
      }}
      defaultValue={''}
      collapse={1}
    />
  );
}

export function DragJsonButton({ nodeData }: { nodeData: NodeData }) {
  function handleDragStart(e: React.DragEvent<HTMLButtonElement>) {
    e.dataTransfer.setData('text/plain', `[token:${nodeData.path.join('.')}]`);
    e.dataTransfer.dropEffect = DropEffect.Copy;
  }

  return (
    <button
      type="button"
      draggable
      onDragStart={handleDragStart}
      className="text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300"
    >
      <DocumentDuplicateIcon className="h-5 w-5" />
    </button>
  );
}
